/* eslint-disable react-hooks/rules-of-hooks */
/**
 * Fachada dos hooks do Callix para as telas do widget.
 *
 * Dentro de um `EngineClientProvider` (modo motor) cada hook lê o `EngineClient`;
 * fora dele, repassa ao `@callixbrasil/client-sdk-react` (SDK no navegador). O modo
 * é fixo durante a vida do componente (o provider não muda), então a escolha de
 * ramo é estável e a ordem de hooks por render também; por isso a regra de hooks
 * condicionais é desligada neste arquivo, e só nele.
 */
import { useContext } from 'react';
import * as sdk from '@callixbrasil/client-sdk-react';
import type { Call, CallInfo, CallOperator, CallOperatorPublicEvents, CallQualification } from '@callixbrasil/client-sdk';
import type { CallOperatorState } from '@callixbrasil/client-sdk-react';
import {
  EngineClientContext,
  useEngineAudioInputMuted,
  useEngineAudioOutputMuted,
  useEngineAudioOutputVolume,
  useEngineControls,
  useEngineCurrentCall,
  useEngineCurrentCallControls,
  useEngineCurrentCallInfo,
  useEngineEventListener,
  useEngineOperator,
  useEngineOperatorState,
  useEngineQualifications,
} from './engineSdk';

export { CallixClientProvider } from '@callixbrasil/client-sdk-react';
export type { CallOperatorState } from '@callixbrasil/client-sdk-react';

type SdkControls = ReturnType<typeof sdk.useCallOperatorControls>;
type SdkCallControls = ReturnType<typeof sdk.useCallOperatorCurrentCallControls>;

export function useCallOperator(): CallOperator {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineOperator(engine) : sdk.useCallOperator();
}

export function useCallOperatorState(): CallOperatorState | undefined {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineOperatorState(engine) : sdk.useCallOperatorState();
}

export function useCallOperatorControls(): SdkControls {
  const engine = useContext(EngineClientContext);
  return engine ? (useEngineControls(engine) as unknown as SdkControls) : sdk.useCallOperatorControls();
}

export function useCallOperatorCurrentCall(): Call | undefined {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineCurrentCall(engine) : sdk.useCallOperatorCurrentCall();
}

export function useCallOperatorCurrentCallInfo(): CallInfo | undefined {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineCurrentCallInfo(engine) : sdk.useCallOperatorCurrentCallInfo();
}

export function useCallOperatorSuccessQualifications(): CallQualification[] {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineQualifications(engine, 'success') : sdk.useCallOperatorSuccessQualifications();
}

export function useCallOperatorDiscardQualifications(): CallQualification[] {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineQualifications(engine, 'discard') : sdk.useCallOperatorDiscardQualifications();
}

export function useCallOperatorCurrentCallControls(): SdkCallControls {
  const engine = useContext(EngineClientContext);
  return engine ? (useEngineCurrentCallControls(engine) as unknown as SdkCallControls) : sdk.useCallOperatorCurrentCallControls();
}

export function useCallOperatorAudioInputMuted(): [boolean, (muted: boolean) => void] {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineAudioInputMuted(engine) : sdk.useCallOperatorAudioInputMuted();
}

export function useCallOperatorAudioOutputMuted(): [boolean, (muted: boolean) => void] {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineAudioOutputMuted(engine) : sdk.useCallOperatorAudioOutputMuted();
}

export function useCallOperatorAudioOutputVolume(): [number, (volume: number) => void] {
  const engine = useContext(EngineClientContext);
  return engine ? useEngineAudioOutputVolume(engine) : sdk.useCallOperatorAudioOutputVolume();
}

export function useCallOperatorEventListener<E extends keyof CallOperatorPublicEvents>(event: E, listener: CallOperatorPublicEvents[E]): void {
  const engine = useContext(EngineClientContext);
  if (engine) useEngineEventListener(engine, event, listener);
  else sdk.useCallOperatorEventListener(event, listener);
}
